import { useEffect } from 'react';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import useClassroomStore from './stores/classroomStore';
import { routeConfig, readerRouteConfig } from './routes';
import { isReaderHost } from './utils/readerEnv';

const READER_HOST = isReaderHost();

function ClockTicker() {
  const tick = useClassroomStore(s => s.tick);
  useEffect(() => {
    const timer = setInterval(tick, 60000);
    return () => clearInterval(timer);
  }, [tick]);
  return null;
}

function HealthCheck() {
  useEffect(() => {
    fetch('/api/health').catch(() => {
      // Backend not available - silent fail
    });
  }, []);
  return null;
}

function AppRoutes() {
  return useRoutes(READER_HOST ? readerRouteConfig : routeConfig);
}

// Set the PWA manifest + iOS A2HS metadata once, based on which subdomain
// the app is served from. The Reader subdomain (reader.lingvishspace.com)
// installs as its own "Lingvish Reader" PWA; the main domain has no PWA
// manifest at all so the marketing landing isn't installable. This runs
// once on mount — no per-route swapping is needed now that each app has
// its own origin.
function applyManifestForHost() {
  if (typeof document === 'undefined') return;

  const head = document.head;
  const existing = head.querySelector('link[rel="manifest"]');

  if (READER_HOST) {
    if (existing) {
      if (!existing.href.endsWith('/reader-manifest.json')) {
        existing.href = '/reader-manifest.json';
      }
    } else {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/reader-manifest.json';
      head.appendChild(link);
    }
    const appleTitle = head.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute('content', 'Reader');
    document.title = 'Lingvish Reader';
  } else if (existing) {
    // Main domain: ensure no manifest is linked so the landing page is
    // never offered as an installable PWA.
    existing.parentNode.removeChild(existing);
  }
}

applyManifestForHost();

// The app used to run on HashRouter, so external links, browser bookmarks,
// and share-sheets still point to `lingvishspace.com/#/some/path`. On boot,
// promote the hash payload to the real pathname so React Router sees
// `/some/path` and old links keep working. Skip when the hash isn't a
// routable path (`#anchor` in-page links must survive).
function migrateLegacyHashUrl() {
  if (typeof window === 'undefined') return;
  const { hash, pathname, search } = window.location;
  if (!hash || !hash.startsWith('#/')) return;
  const legacyPath = hash.slice(1);  // "#/foo" → "/foo"
  const rewritten = legacyPath + search;
  if (pathname === '/' || pathname === '') {
    window.history.replaceState({}, '', rewritten);
  }
}

migrateLegacyHashUrl();

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <HealthCheck />
        <ClockTicker />
        <AppRoutes />
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#2A4D25',
            color: '#fff',
            borderRadius: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            maxWidth: '420px',
          },
          success: { iconTheme: { primary: '#fff', secondary: '#2A4D25' } },
          error: {
            style: { background: '#BF3337' },
            iconTheme: { primary: '#fff', secondary: '#BF3337' },
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
